import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { generateCoverLetter, extractKeywords } from '../services/openaiService';
import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import * as mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import { useUser } from '../contexts/UserContext';
import './ResumeBuilderLanding.css';
import './CoverLetterStudio.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const TEMPLATE_OPTIONS = [
  { id: 'classic',  name: 'Classic',  accent: '#e0a3ff' },
  { id: 'modern',   name: 'Modern',   accent: '#00ffff' },
  { id: 'minimal',  name: 'Minimal',  accent: '#9370db' },
];

// Wizard steps. Titles/subtitles render inside the chevron header at the top
// of the left column; the number doubles as the completion tick.
const STEPS = [
  { n: 1, title: 'Personal Info', sub: 'Your details & resume' },
  { n: 2, title: 'Job Details',   sub: 'Describe the position' },
  { n: 3, title: 'Cover Letter',  sub: 'Generate & edit' },
];

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <label className="rbs-field">
    <span>{label}</span>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </label>
);

const TextArea = ({ label, value, onChange, placeholder, rows = 4 }) => (
  <label className="rbs-field">
    <span>{label}</span>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
    />
  </label>
);

// Build a resume-text blob from the user's profile so the generator has the
// same substrate whether the user uploads a resume or picks "use my profile".
const buildResumeTextFromProfile = (profile) => {
  if (!profile) return '';
  let text = '';
  if (profile.fullName) text += `${profile.fullName}\n`;
  if (profile.jobTitle) text += `${profile.jobTitle}\n`;
  if (profile.email)    text += `${profile.email}\n`;
  if (profile.phone)    text += `Phone: ${profile.phone}\n`;
  if (profile.location) text += `Location: ${profile.location}\n`;
  if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
  text += '\n';
  if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
  if (profile.skills)              text += `SKILLS\n${profile.skills}\n\n`;
  if (Array.isArray(profile.workExperience)) {
    const meaningful = profile.workExperience.filter((e) => e.company || e.position);
    if (meaningful.length) {
      text += 'WORK EXPERIENCE\n';
      meaningful.forEach((exp) => {
        text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}`;
        if (exp.duration) text += ` (${exp.duration})`;
        text += '\n';
        if (exp.description) text += `${exp.description}\n`;
        text += '\n';
      });
    }
  }
  if (Array.isArray(profile.education)) {
    const meaningful = profile.education.filter((e) => e.degree || e.institution);
    if (meaningful.length) {
      text += 'EDUCATION\n';
      meaningful.forEach((edu) => {
        text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}`;
        if (edu.institution) text += ` - ${edu.institution}`;
        if (edu.year)        text += ` (${edu.year})`;
        text += '\n';
      });
    }
  }
  return text.trim();
};

const CoverLetterStudio = ({ setShowAuthModal }) => {
  const fileRef = useRef(null);
  const { user, credits, setCredits, fetchCredits } = useUser();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  // ── state ──────────────────────────────────────────────────────
  const [jobDescription, setJobDescription] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [showKeywords, setShowKeywords] = useState(false);

  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [uploadedName, setUploadedName] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [resumeSource, setResumeSource] = useState(null); // 'profile' | 'upload' | null
  const [profileData, setProfileData] = useState(null);

  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');

  const [jobDetails, setJobDetails] = useState({
    companyName: '',
    hiringManagerName: '',
    jobTitle: '',
  });
  const [personalInfo, setPersonalInfo] = useState({
    fullName: '', email: '', phone: '', location: '', linkedIn: '',
  });

  const [coverLetter, setCoverLetter] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');

  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadErr, setDownloadErr] = useState('');
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);

  // ── profile fetch ──────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/profile`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.profile) return;
        setProfileData(data.profile);
        setPersonalInfo((prev) => ({
          fullName: prev.fullName || data.profile.fullName || '',
          email:    prev.email    || data.profile.email    || '',
          phone:    prev.phone    || data.profile.phone    || '',
          location: prev.location || data.profile.location || '',
          linkedIn: prev.linkedIn || data.profile.linkedIn || '',
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, API_BASE_URL]);

  const hasProfileResume = !!(
    profileData && (
      profileData.professionalSummary ||
      profileData.skills ||
      (Array.isArray(profileData.workExperience) &&
        profileData.workExperience.some((e) => e.company || e.position))
    )
  );

  // ── credit + auth ──────────────────────────────────────────────
  const requireAuthAndCredits = useCallback(() => {
    if (!user) {
      if (typeof setShowAuthModal === 'function') setShowAuthModal(true);
      else setGenError('Please sign in to continue.');
      return false;
    }
    if (credits < 1) {
      setShowInsufficientCredits(true);
      return false;
    }
    return true;
  }, [user, credits, setShowAuthModal]);

  const deductCredit = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/cover-letter/deduct-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403) {
          setShowInsufficientCredits(true);
          return false;
        }
        throw new Error(data.error || 'Failed to deduct credit');
      }
      if (setCredits) setCredits(data.remainingCredits);
      return true;
    } catch (e) {
      setGenError(`Credit error: ${e.message}`);
      return false;
    }
  }, [API_BASE_URL, setCredits]);

  // ── resume input ───────────────────────────────────────────────
  const extractPdfText = async (file) => {
    const buf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    let text = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((it) => it.str).join(' ') + '\n';
    }
    return text.trim();
  };

  const extractDocxText = async (file) => {
    const buf = await file.arrayBuffer();
    const r = await mammoth.extractRawText({ arrayBuffer: buf });
    return r.value.trim();
  };

  const readResumeFile = useCallback(async (file) => {
    if (!file) return;
    const okTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    if (!okTypes.includes(file.type)) {
      setParseError('Please upload a PDF or DOCX file.');
      return;
    }
    setParseError('');
    setParsing(true);
    setUploadedName(file.name);
    try {
      const text = file.type === 'application/pdf'
        ? await extractPdfText(file)
        : await extractDocxText(file);
      if (!text) throw new Error('No text extracted');
      setResumeText(text);
      setResumeSource('upload');
    } catch (e) {
      setParseError(`Failed to read file: ${e.message}`);
      setUploadedName('');
    } finally {
      setParsing(false);
    }
  }, []);

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) readResumeFile(f);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) readResumeFile(f);
  };
  const onDragOver = (e) => e.preventDefault();

  const handleUseProfile = () => {
    if (!hasProfileResume) return;
    const text = buildResumeTextFromProfile(profileData);
    if (!text) return;
    setResumeText(text);
    setResumeSource('profile');
    setUploadedName('Profile data');
  };

  // ── keywords ───────────────────────────────────────────────────
  const handleExtractKeywords = useCallback(async () => {
    if (!jobDescription.trim()) {
      setGenError('Paste a job description first.');
      return;
    }
    if (!user) {
      if (typeof setShowAuthModal === 'function') setShowAuthModal(true);
      else setGenError('Please sign in to continue.');
      return;
    }
    setGenError('');
    setExtracting(true);
    try {
      const kws = await extractKeywords(jobDescription);
      setKeywords(Array.isArray(kws) ? kws : []);
    } catch (e) {
      setGenError(`Could not extract keywords: ${e.message}`);
    } finally {
      setExtracting(false);
    }
  }, [jobDescription, user, setShowAuthModal]);

  // ── generate ───────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    setGenError('');
    if (!requireAuthAndCredits()) return;
    if (!resumeText.trim()) {
      setGenError('Choose a resume source first (upload or profile).');
      return;
    }
    if (!jobDescription.trim()) {
      setGenError('Paste a job description first.');
      return;
    }
    if (!jobDetails.jobTitle.trim()) {
      setGenError('Enter the job title.');
      return;
    }
    setGenerating(true);
    try {
      const ok = await deductCredit();
      if (!ok) { setGenerating(false); return; }

      const kws = keywords.length ? keywords : await extractKeywords(jobDescription);
      if (!keywords.length) setKeywords(Array.isArray(kws) ? kws : []);

      const letter = await generateCoverLetter(
        resumeText,
        jobDescription,
        kws,
        jobDetails.companyName,
        jobDetails.hiringManagerName,
        jobDetails.jobTitle,
        personalInfo,
      );
      setCoverLetter(letter);

      // Fire-and-forget save
      fetch(`${API_BASE_URL}/api/cover-letter/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          coverLetterContent: letter,
          jobTitle: jobDetails.jobTitle,
          companyName: jobDetails.companyName,
          jobDescription,
          keywords: kws,
          personalInfo,
          fileName: `Cover_Letter_${(jobDetails.jobTitle || 'Untitled').replace(/\s+/g, '_')}_${(jobDetails.companyName || 'Company').replace(/\s+/g, '_')}.docx`,
        }),
      }).catch(() => {});

      if (fetchCredits) fetchCredits();
    } catch (e) {
      setGenError(`Generation failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  }, [requireAuthAndCredits, resumeText, jobDescription, jobDetails, personalInfo, keywords, deductCredit, fetchCredits, API_BASE_URL]);

  // ── downloads ──────────────────────────────────────────────────
  const buildFileBase = () => {
    const t = (jobDetails.jobTitle || 'Untitled').replace(/\s+/g, '_');
    const c = (jobDetails.companyName || 'Company').replace(/\s+/g, '_');
    return `Cover_Letter_${t}_${c}`;
  };

  const handleDownloadDocx = useCallback(async () => {
    if (!coverLetter) return;
    setDownloading(true);
    setDownloadErr('');
    try {
      const children = [];
      const lines = coverLetter.split('\n');
      let i = 0;
      while (i < lines.length && !lines[i].trim()) i++;
      if (i < lines.length) {
        children.push(new Paragraph({
          children: [new TextRun({ text: lines[i].trim(), font: 'Times New Roman', size: 36, bold: true })],
          spacing: { after: 120, line: 360 },
          alignment: AlignmentType.CENTER,
        }));
        i++;
      }
      while (i < lines.length && !lines[i].trim()) i++;
      if (i < lines.length) {
        children.push(new Paragraph({
          children: [new TextRun({ text: lines[i].trim(), font: 'Times New Roman', size: 24 })],
          spacing: { after: 360, line: 360 },
          alignment: AlignmentType.CENTER,
        }));
        i++;
      }
      for (; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        if (/^sincerely/i.test(line)) {
          children.push(new Paragraph({
            children: [new TextRun({ text: line, font: 'Times New Roman', size: 24 })],
            spacing: { before: 240, after: 0, line: 360 },
          }));
          if (personalInfo.fullName) {
            children.push(new Paragraph({
              children: [new TextRun({ text: personalInfo.fullName, font: 'Times New Roman', size: 24 })],
              spacing: { before: 0, line: 360 },
            }));
          }
          break;
        }
        children.push(new Paragraph({
          children: [new TextRun({ text: line, font: 'Times New Roman', size: 24 })],
          spacing: { after: 180, line: 360 },
        }));
      }
      const doc = new Document({
        sections: [{
          properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
          children,
        }],
      });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${buildFileBase()}.docx`);
    } catch (e) {
      setDownloadErr(`DOCX export failed: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverLetter, personalInfo.fullName, jobDetails.jobTitle, jobDetails.companyName]);

  const handleDownloadPdf = useCallback(async () => {
    if (!coverLetter) return;
    setDownloadingPdf(true);
    setDownloadErr('');
    try {
      // Text-based PDF (selectable/ATS-parseable). A4 portrait, 1-inch margins.
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const PAGE_W = 595.28;
      const PAGE_H = 841.89;
      const M = 72; // 1"
      const CW = PAGE_W - M * 2;
      let y = M;

      const lines = coverLetter.split('\n').map((l) => l.trim());
      let i = 0;
      while (i < lines.length && !lines[i]) i++;

      // Header: name (bold, centered)
      if (i < lines.length) {
        doc.setFont('times', 'bold');
        doc.setFontSize(18);
        doc.text(lines[i], PAGE_W / 2, y, { align: 'center' });
        y += 24;
        i++;
      }
      while (i < lines.length && !lines[i]) i++;
      // Contact line (centered)
      if (i < lines.length) {
        doc.setFont('times', 'normal');
        doc.setFontSize(11);
        doc.text(lines[i], PAGE_W / 2, y, { align: 'center' });
        y += 28;
        i++;
      }

      doc.setFont('times', 'normal');
      doc.setFontSize(12);
      const lineH = 16;

      const writeParagraph = (text) => {
        const wrapped = doc.splitTextToSize(text, CW);
        for (const w of wrapped) {
          if (y + lineH > PAGE_H - M) { doc.addPage(); y = M; }
          doc.text(w, M, y);
          y += lineH;
        }
        y += 6; // gap between paragraphs
      };

      for (; i < lines.length; i++) {
        const line = lines[i];
        if (!line) continue;
        if (/^sincerely/i.test(line)) {
          y += 10;
          writeParagraph(line);
          if (personalInfo.fullName) writeParagraph(personalInfo.fullName);
          break;
        }
        writeParagraph(line);
      }

      doc.save(`${buildFileBase()}.pdf`);
    } catch (e) {
      setDownloadErr(`PDF export failed: ${e.message}`);
    } finally {
      setDownloadingPdf(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverLetter, personalInfo.fullName, jobDetails.jobTitle, jobDetails.companyName]);

  // ── derived ────────────────────────────────────────────────────
  const jobDone = !!(jobDetails.jobTitle && jobDescription);
  const personalDone = !!(personalInfo.fullName && personalInfo.email);
  // Drives the tick shown in each chevron once you've moved past it.
  const stepDone = {
    1: personalDone && !!resumeText,
    2: jobDone,
    3: !!coverLetter,
  };

  const canGenerate = !!(user && resumeText && jobDescription && jobDetails.jobTitle && !generating);
  const ctaLabel = generating ? 'Generating…' : coverLetter ? 'Regenerate' : 'Generate Cover Letter';

  // Parse letter into structured pieces so each template can render its own
  // header/body layout. Falls back gracefully on malformed input.
  const parsedLetter = useMemo(() => {
    if (!coverLetter) return null;
    const lines = coverLetter.split('\n');
    let i = 0;
    while (i < lines.length && !lines[i].trim()) i++;
    const name = i < lines.length ? lines[i].trim() : '';
    i++;
    while (i < lines.length && !lines[i].trim()) i++;
    const contact = i < lines.length ? lines[i].trim() : '';
    i++;
    const body = [];
    let signOff = '';
    for (; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) { body.push(''); continue; }
      if (/^sincerely/i.test(line)) { signOff = line; break; }
      body.push(line);
    }
    // Collapse consecutive blanks + trim leading/trailing blanks
    const paragraphs = body
      .join('\n')
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);
    return { name, contact, paragraphs, signOff, signatureName: personalInfo.fullName || name };
  }, [coverLetter, personalInfo.fullName]);

  const sourceLocked = !user;

  const showKeywordsModal = showKeywords && keywords.length > 0;

  return (
    <div className="cls-root">
      {showInsufficientCredits && (
        <div className="cls-modal-overlay" onClick={() => setShowInsufficientCredits(false)}>
          <div className="cls-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Insufficient Credits</h3>
            <p>You need at least 1 credit to generate a cover letter.</p>
            <p>Current credits: <strong>{credits}</strong></p>
            <div className="cls-modal-actions">
              <button
                className="rbl-btn rbl-btn-primary"
                onClick={() => {
                  setShowInsufficientCredits(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}
              >
                Get More Credits
              </button>
              <button
                className="rbl-btn rbl-btn-ghost"
                onClick={() => setShowInsufficientCredits(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keywords popup. The overlay and card classes must be `rbs-modal-overlay`
          and `rbs-modal` — `rbs-kw-modal` on its own only sets a max-width, so
          using it alone rendered this inline in the page flow with no backdrop,
          no card and no z-index. Same pairing the Resume Studio uses. */}
      {showKeywordsModal && (
        <div className="rbs-modal-overlay" onClick={() => setShowKeywords(false)}>
          <div className="rbs-modal rbs-kw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rbs-kw-modal-head">
              <h3 className="cls-kw-title">Extracted Keywords</h3>
              <span className="rbs-kw-modal-count">{keywords.length}</span>
              <button
                className="cls-kw-close"
                onClick={() => setShowKeywords(false)}
                aria-label="Close keywords"
              >
                ×
              </button>
            </div>
            <p className="rbs-kw-modal-sub">These are the terms the AI pulled from the job description — the letter will weave them in naturally.</p>
            <div className="rbs-kw-modal-list">
              {keywords.map((k, i) => <span key={i} className="rbs-kw">{k}</span>)}
            </div>
          </div>
        </div>
      )}

      <div className="rbs-workspace">
        {/* LEFT: form */}
        <div className="rbs-left">
          {/* Chevron step header. Every step stays clickable — the wizard
              orders the work, it doesn't lock you out of it. */}
          <nav className="cls-steps" aria-label="Cover letter steps">
            {STEPS.map((s, i) => (
              <button
                key={s.n}
                type="button"
                className={`cls-step ${step === s.n ? 'is-active' : ''} ${stepDone[s.n] ? 'is-done' : ''}`}
                style={{ '--i': i }}
                onClick={() => setStep(s.n)}
                aria-current={step === s.n ? 'step' : undefined}
              >
                <span className="cls-step-inner">
                  <span className="cls-step-n">{stepDone[s.n] && step !== s.n ? '✓' : s.n}</span>
                  <span className="cls-step-text">
                    <span className="cls-step-title">{s.title}</span>
                    <span className="cls-step-sub">{s.sub}</span>
                  </span>
                </span>
              </button>
            ))}
          </nav>

          <div className="rbs-left-scroll">
            {/* ══════════ STEP 1 — Personal Info ══════════ */}
            {step === 1 && (
              <>
                <div className="cls-details">
                  <div className="cls-details-head">
                    <span className="cls-details-title">Your contact details</span>
                    {personalDone && <span className="cls-details-check" aria-label="complete">✓</span>}
                  </div>
                  <div className="rbs-grid-2">
                    <Field label="Full Name" value={personalInfo.fullName} onChange={(v) => setPersonalInfo((p) => ({ ...p, fullName: v }))} placeholder="Jane Doe" />
                    <Field label="Email" type="email" value={personalInfo.email} onChange={(v) => setPersonalInfo((p) => ({ ...p, email: v }))} placeholder="jane@email.com" />
                  </div>
                  <div className="rbs-grid-2">
                    <Field label="Phone" value={personalInfo.phone} onChange={(v) => setPersonalInfo((p) => ({ ...p, phone: v }))} placeholder="(555) 555-1234" />
                    <Field label="Location" value={personalInfo.location} onChange={(v) => setPersonalInfo((p) => ({ ...p, location: v }))} placeholder="San Francisco, CA" />
                  </div>
                  <Field label="LinkedIn" value={personalInfo.linkedIn} onChange={(v) => setPersonalInfo((p) => ({ ...p, linkedIn: v }))} placeholder="linkedin.com/in/janedoe" />
                </div>

                {/* Resume lives with Personal Info — it's your material, not
                    the employer's. The JD is what belongs to step 2. */}
                <div className="cls-details">
                  <div className="cls-details-head">
                    <span className="cls-details-title">Your resume</span>
                    {!!resumeText && <span className="cls-details-check" aria-label="complete">✓</span>}
                  </div>
                  <div className={`rbs-source ${hasProfileResume ? 'has-profile' : ''} ${sourceLocked ? 'is-locked' : ''}`}>
                    <div
                      className={`rbs-drop ${parsing ? 'busy' : ''} ${sourceLocked ? 'locked' : ''}`}
                      onDrop={sourceLocked ? undefined : onDrop}
                      onDragOver={sourceLocked ? undefined : onDragOver}
                      onClick={() => { if (!sourceLocked) fileRef.current?.click(); }}
                      role="button"
                      tabIndex={0}
                    >
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        hidden
                        onChange={onFileInput}
                      />
                      <div className="rbs-drop-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M12 16V4M12 4L7 9M12 4l5 5M5 20h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="rbs-drop-title">
                        {parsing
                          ? 'Reading your resume…'
                          : resumeSource === 'upload' && uploadedName
                            ? `Loaded: ${uploadedName}`
                            : 'Upload PDF or DOCX'}
                      </div>
                      <div className="rbs-drop-sub">
                        {parsing
                          ? 'Extracting text'
                          : sourceLocked
                            ? 'Sign in first to upload a resume'
                            : 'Drag & drop or click to select'}
                      </div>
                    </div>
                    {hasProfileResume && (
                      <button
                        type="button"
                        className={`rbs-source-alt rbs-source-profile ${resumeSource === 'profile' ? 'is-active' : ''}`}
                        onClick={handleUseProfile}
                        disabled={parsing}
                      >
                        <span>{resumeSource === 'profile' ? 'Using Profile ✓' : 'Use my Profile data'}</span>
                        <svg width="14" height="14" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
                      </button>
                    )}
                  </div>
                  {parseError && <div className="rbs-error">{parseError}</div>}
                </div>
              </>
            )}

            {/* ══════════ STEP 2 — Job Details ══════════ */}
            {step === 2 && (
              <>
                {/* Job description bar */}
                <div className="rbs-jdbar">
                  <div className="rbs-jdbar-label">
                    <span className="rbs-jdbar-title">Job Description</span>
                    <span className="rbs-jdbar-hint">Paste the JD — the AI weaves its keywords into your letter</span>
                  </div>
                  <div className="rbs-jdbar-input">
                    <textarea
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      placeholder="Paste the complete job description here…"
                      rows={1}
                    />
                  </div>
                  <div className="rbs-jdbar-actions">
                    <button
                      type="button"
                      className="rbl-btn rbl-btn-primary rbs-jdbar-btn"
                      onClick={handleExtractKeywords}
                      disabled={extracting}
                    >
                      {extracting ? 'Extracting…' : 'Extract Keywords'}
                    </button>
                    <button
                      type="button"
                      className="rbl-btn rbl-btn-ghost rbs-jdbar-btn"
                      onClick={() => setShowKeywords(true)}
                      disabled={keywords.length === 0}
                      title={keywords.length === 0 ? 'Extract keywords first' : `Show ${keywords.length} extracted keywords`}
                    >
                      Show Keywords{keywords.length > 0 ? ` (${keywords.length})` : ''}
                    </button>
                  </div>
                </div>

                <div className="cls-details">
                  <div className="cls-details-head">
                    <span className="cls-details-title">The role</span>
                    {jobDone && <span className="cls-details-check" aria-label="complete">✓</span>}
                  </div>
                  <div className="rbs-grid-2">
                    <Field
                      label="Job Title *"
                      value={jobDetails.jobTitle}
                      onChange={(v) => setJobDetails((p) => ({ ...p, jobTitle: v }))}
                      placeholder="e.g., Senior Software Engineer"
                    />
                    <Field
                      label="Company"
                      value={jobDetails.companyName}
                      onChange={(v) => setJobDetails((p) => ({ ...p, companyName: v }))}
                      placeholder="e.g., Stripe"
                    />
                  </div>
                  <Field
                    label="Hiring Manager (optional)"
                    value={jobDetails.hiringManagerName}
                    onChange={(v) => setJobDetails((p) => ({ ...p, hiringManagerName: v }))}
                    placeholder="e.g., Jane Smith"
                  />
                </div>
              </>
            )}

            {/* ══════════ STEP 3 — Cover Letter ══════════ */}
            {step === 3 && (
              <div className="cls-details cls-details--letter">
                <div className="cls-details-head">
                  <span className="cls-details-title">Letter content</span>
                  {!!coverLetter && <span className="cls-details-check" aria-label="complete">✓</span>}
                </div>

                {coverLetter ? (
                  <>
                    <p className="cls-step-note">
                      Edit the text below — the preview and both downloads update as you type.
                    </p>
                    <textarea
                      className="cls-letter-edit"
                      value={coverLetter}
                      onChange={(e) => setCoverLetter(e.target.value)}
                      spellCheck
                    />
                  </>
                ) : (
                  <div className="cls-ready">
                    <p className="cls-step-note">
                      Everything the AI needs, at a glance. Generate when the list is green.
                    </p>
                    <ul className="cls-ready-list">
                      <li className={resumeText ? 'ok' : ''}>
                        <span>{resumeText ? '✓' : '○'}</span> Resume loaded
                      </li>
                      <li className={jobDescription ? 'ok' : ''}>
                        <span>{jobDescription ? '✓' : '○'}</span> Job description pasted
                      </li>
                      <li className={jobDetails.jobTitle ? 'ok' : ''}>
                        <span>{jobDetails.jobTitle ? '✓' : '○'}</span> Job title set
                      </li>
                      <li className={keywords.length ? 'ok' : ''}>
                        <span>{keywords.length ? '✓' : '○'}</span>
                        {keywords.length ? `${keywords.length} keywords extracted` : 'Keywords (optional)'}
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Generate CTA — a sibling of the scroll area, not inside it, so it
              stays pinned to the bottom of the column however far the form
              above is scrolled. */}
          <div className="cls-generate-row">
            <div className="cls-nav">
              {step > 1 && (
                <button
                  type="button"
                  className="rbl-btn rbl-btn-ghost cls-back-btn"
                  onClick={() => setStep((s) => s - 1)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M11 19l-7-7 7-7"/></svg>
                  Back
                </button>
              )}

              {step < 3 ? (
                <button
                  type="button"
                  className="rbl-btn rbl-btn-primary cls-generate-btn"
                  onClick={() => setStep((s) => s + 1)}
                >
                  Save &amp; next
                  <svg width="16" height="16" viewBox="0 0 24 24">
                    <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/>
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  className="rbl-btn rbl-btn-primary cls-generate-btn"
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  title={
                    !user ? 'Sign in to generate' :
                    !resumeText ? 'Choose a resume source first' :
                    !jobDescription ? 'Paste a job description' :
                    !jobDetails.jobTitle ? 'Enter a job title' : 'Generate a tailored cover letter (1 credit)'
                  }
                >
                  {ctaLabel}
                  {!generating && (
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/>
                    </svg>
                  )}
                </button>
              )}
            </div>
            {genError && <div className="rbs-error cls-gen-error">{genError}</div>}
          </div>
        </div>

        {/* RIGHT: preview */}
        <div className="rbs-right">
          <div
            className="rbs-rightcard"
            style={{ '--accent': (TEMPLATE_OPTIONS.find((t) => t.id === selectedTemplate) || TEMPLATE_OPTIONS[0]).accent }}
          >
            <div className="rbs-tpl-row">
              <span className="rbs-tpl-label">Template</span>
              <div className="rbs-tpl-pills">
                {TEMPLATE_OPTIONS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`rbs-tpl ${selectedTemplate === t.id ? 'active' : ''}`}
                    style={{ '--accent': t.accent }}
                    onClick={() => setSelectedTemplate(t.id)}
                  >
                    {t.name}
                  </button>
                ))}
              </div>

              {/* Theme switch. Rides the template row's top-right rather than
                  taking its own strip, which would cost preview height. */}
            </div>

            <div className="rbs-preview">
              <div className={`rbs-preview-viewport ${!parsedLetter ? 'is-empty' : ''}`}>
                {parsedLetter ? (
                  <CoverLetterPaper letter={parsedLetter} template={selectedTemplate} />
                ) : (
                  <div className="rbs-paper rbs-paper--empty">
                    <div className="rbs-paper-empty">
                      <div className="rbs-paper-empty-icon">✉️</div>
                      <div className="rbs-paper-empty-title">Your cover letter will appear here</div>
                      <div className="rbs-paper-empty-sub">
                        Paste the JD, pick your resume, fill in the job details, then hit
                        <strong> Generate</strong>. The letter renders live in the template you pick above.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rbs-actions">
              <div className="rbs-download-row">
                <button
                  type="button"
                  className="rbl-btn rbl-btn-primary rbs-download"
                  onClick={handleDownloadDocx}
                  disabled={downloading || !coverLetter}
                  title={coverLetter ? 'Download as Word (.docx)' : 'Generate a letter first'}
                >
                  {downloading ? 'Building DOCX…' : 'Download .docx'}
                  {!downloading && (
                    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 4v12M6 10l6 6 6-6M5 20h14"/></svg>
                  )}
                </button>
                <button
                  type="button"
                  className="rbl-btn rbl-btn-primary rbs-download rbs-download--pdf"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || !coverLetter}
                  title={coverLetter ? 'Download as PDF' : 'Generate a letter first'}
                >
                  {downloadingPdf ? 'Building PDF…' : 'Download PDF'}
                  {!downloadingPdf && (
                    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 4v12M6 10l6 6 6-6M5 20h14"/></svg>
                  )}
                </button>
              </div>
              {downloadErr && <div className="rbs-error">{downloadErr}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Template renderers ────────────────────────────────────────────
// Each template consumes the same parsed letter shape and applies a
// distinct visual treatment. The underlying text is identical — only
// typography, alignment and header ornament change.
const CoverLetterPaper = ({ letter, template }) => {
  if (template === 'modern')  return <ModernLetter letter={letter} />;
  if (template === 'minimal') return <MinimalLetter letter={letter} />;
  return <ClassicLetter letter={letter} />;
};

const ClassicLetter = ({ letter }) => (
  <div className="rbs-paper cls-paper cls-paper--classic">
    <div className="cls-classic-header">
      <div className="cls-classic-name">{letter.name}</div>
      <div className="cls-classic-contact">{letter.contact}</div>
    </div>
    <div className="cls-body">
      {letter.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </div>
    {letter.signOff && (
      <div className="cls-signoff">
        <div>{letter.signOff}</div>
        {letter.signatureName && <div className="cls-signature">{letter.signatureName}</div>}
      </div>
    )}
  </div>
);

const ModernLetter = ({ letter }) => (
  <div className="rbs-paper cls-paper cls-paper--modern">
    <div className="cls-modern-header">
      <div className="cls-modern-name">{letter.name}</div>
      <div className="cls-modern-bar" />
      <div className="cls-modern-contact">{letter.contact}</div>
    </div>
    <div className="cls-body cls-body--sans">
      {letter.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </div>
    {letter.signOff && (
      <div className="cls-signoff cls-signoff--sans">
        <div>{letter.signOff}</div>
        {letter.signatureName && <div className="cls-signature cls-signature--modern">{letter.signatureName}</div>}
      </div>
    )}
  </div>
);

const MinimalLetter = ({ letter }) => (
  <div className="rbs-paper cls-paper cls-paper--minimal">
    <div className="cls-minimal-header">
      <span className="cls-minimal-name">{letter.name}</span>
      {letter.contact && <span className="cls-minimal-sep">·</span>}
      <span className="cls-minimal-contact">{letter.contact}</span>
    </div>
    <div className="cls-body cls-body--sans">
      {letter.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </div>
    {letter.signOff && (
      <div className="cls-signoff cls-signoff--sans">
        <div>{letter.signOff}</div>
        {letter.signatureName && <div className="cls-signature cls-signature--minimal">{letter.signatureName}</div>}
      </div>
    )}
  </div>
);

export default CoverLetterStudio;
